/**
 * useBilling — the canonical billing-state hook.
 *
 * Holds a single cached ``BillingState`` per app session, refetched on
 * auth-state change and on every successful billing action. Every UI
 * surface that needs to know whether the user is trialing / locked /
 * needsConsent etc. consumes this hook rather than re-deriving state.
 *
 * Backs launch-compliance R1.3.
 */

import { useCallback, useEffect, useState } from "react";
import {
  attachCard as apiAttachCard,
  cancelSubscription as apiCancel,
  changePlan as apiChangePlan,
  getBillingState,
  grantConsent as apiGrantConsent,
  reactivateSubscription as apiReactivate,
  requestConsent as apiRequestConsent,
  revokeConsent as apiRevokeConsent,
  setDirectoryOptin as apiSetDirectoryOptin,
  setSeats as apiSetSeats,
  setSocialOptin as apiSetSocialOptin,
  startFree as apiStartFree,
  startTrial as apiStartTrial,
  startTrialNoCard as apiStartTrialNoCard,
  subscribeNow as apiSubscribeNow,
} from "./api";
import type { BillingState, BillingView } from "./types";
import { billingView } from "./billingView";
import { createPlatformAdapter } from "../platform";

// Top-level cache shared across every useBilling caller in this app
// session. A tiny pub-sub keeps every mounted hook in sync after any
// action.
let _cached: BillingState | null = null;
let _loading = false;
const _listeners = new Set<(s: BillingState | null) => void>();

function _broadcast(state: BillingState | null) {
  _cached = state;
  for (const fn of _listeners) fn(state);
}

async function _refetch(): Promise<BillingState | null> {
  if (_loading) {
    // Coalesce: every concurrent caller awaits the same in-flight refetch.
    return new Promise((resolve) => {
      const unsub = (state: BillingState | null) => {
        _listeners.delete(unsub);
        resolve(state);
      };
      _listeners.add(unsub);
    });
  }
  _loading = true;
  _lastFetchAt = Date.now();
  try {
    const state = await getBillingState();
    _broadcast(state);
    return state;
  } finally {
    _loading = false;
  }
}

// The plan can change outside the app (a downgrade on Apple's subscription
// sheet, build 8 pass 2026-10-24), so it is read again when the app comes back
// to the foreground, at most once a minute. iOS sends both Capacitor's "resume"
// and visibilitychange for one return; the throttle makes that one fetch.
export const FOREGROUND_REFRESH_MS = 60_000;
let _lastFetchAt = 0;
let _mounted = 0;
let _foregroundOff: (() => void) | null = null;

function _onForeground(): void {
  if (_cached === null || Date.now() - _lastFetchAt < FOREGROUND_REFRESH_MS) return;
  void _refetch().catch(() => {});
}

function _watchForeground(): () => void {
  const onVisible = () => {
    if (document.visibilityState === "visible") _onForeground();
  };
  document.addEventListener("visibilitychange", onVisible);
  const offResume = createPlatformAdapter().onAppResume?.(_onForeground);
  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    offResume?.();
  };
}

/**
 * The last billing state any caller fetched, without fetching — for a click
 * handler that must not wait on the network (VoiceControls' seat check).
 */
export function cachedBillingState(): BillingState | null {
  return _cached;
}

export function refreshBilling(): Promise<BillingState | null> {
  return _refetch();
}

/**
 * Hand every mounted ``useBilling`` a state a non-hook caller already has —
 * the App Store path (`appStore.ts`) gets one back from each posted transaction.
 */
export function publishBillingState(state: BillingState): void {
  _broadcast(state);
}

export interface UseBillingValue extends Partial<BillingState> {
  isReady: boolean;
  needsConsent: boolean;
  /**
   * camelCase component-facing projection of the current ``BillingState``
   * (``null`` until loaded). Ported design components read this so they get
   * exactly the fields the handoff documents (``status``, ``daysLeft``,
   * ``locked``, ``isFree``, ``trialNoCard``, ``hasCard``, ``consent``,
   * ``kind``, ``plan``…).
   */
  view: BillingView | null;
  refresh: () => Promise<BillingState | null>;
  startTrial: (args: { plan_id: string; payment_method_id?: string; card_brand?: string; card_last4?: string }) => Promise<BillingState>;
  startTrialNoCard: (plan_id?: string) => Promise<BillingState>;
  attachCard: (args: { payment_method_id: string; card_brand?: string; card_last4?: string }) => Promise<BillingState>;
  startFree: () => Promise<BillingState>;
  requestConsent: () => Promise<BillingState>;
  grantConsent: (terms_version: number) => Promise<BillingState>;
  revokeConsent: () => Promise<BillingState>;
  subscribeNow: (args: { plan_id: string; payment_method_id?: string; card_brand?: string; card_last4?: string }) => Promise<BillingState>;
  cancel: () => Promise<BillingState>;
  reactivate: () => Promise<BillingState>;
  changePlan: (plan_id: string, kept_user_ids?: string[]) => Promise<BillingState>;
  /** Who holds the seats, without a plan change (POST /billing/seats). */
  setSeats: (kept_user_ids: string[]) => Promise<BillingState>;
  setSocialOptin: (enabled: boolean) => Promise<BillingState>;
  setDirectoryOptin: (enabled: boolean) => Promise<BillingState>;
}

export function useBilling(): UseBillingValue {
  const [state, setState] = useState<BillingState | null>(_cached);

  useEffect(() => {
    _listeners.add(setState);
    if (_cached === null && !_loading) {
      void _refetch();
    }
    if (_mounted++ === 0) _foregroundOff = _watchForeground();
    return () => {
      _listeners.delete(setState);
      if (--_mounted === 0) {
        _foregroundOff?.();
        _foregroundOff = null;
      }
    };
  }, []);

  const wrap = useCallback(
    async <A extends unknown[]>(
      fn: (...args: A) => Promise<BillingState>,
      ...args: A
    ): Promise<BillingState> => {
      const next = await fn(...args);
      _broadcast(next);
      return next;
    },
    [],
  );

  const needsConsent =
    state !== null && state.kind === "kid" && state.consent !== "granted";

  return {
    ...(state || {}),
    isReady: state !== null,
    needsConsent,
    view: state !== null ? billingView(state) : null,
    refresh: _refetch,
    startTrial: (args) => wrap(apiStartTrial, args),
    startTrialNoCard: (plan_id) => wrap(apiStartTrialNoCard, plan_id),
    attachCard: (args) => wrap(apiAttachCard, args),
    startFree: () => wrap(apiStartFree),
    requestConsent: () => wrap(apiRequestConsent),
    grantConsent: (terms_version) => wrap(apiGrantConsent, terms_version),
    revokeConsent: () => wrap(apiRevokeConsent),
    subscribeNow: (args) => wrap(apiSubscribeNow, args),
    cancel: () => wrap(apiCancel),
    reactivate: () => wrap(apiReactivate),
    changePlan: (plan_id, kept_user_ids) => wrap(apiChangePlan, plan_id, kept_user_ids),
    setSeats: (kept_user_ids) => wrap(apiSetSeats, kept_user_ids),
    setSocialOptin: (enabled) => wrap(apiSetSocialOptin, enabled),
    setDirectoryOptin: (enabled) => wrap(apiSetDirectoryOptin, enabled),
  };
}
